import React, { useState } from "react";
import * as XLSX from "xlsx";
import MainLayout from "../../layouts/MainLayout";
import { createStaff } from "../../api/staffApi";
import "../Attendance/Attendance.css";

// Matches digicampusbd's real fixed column headers. Note: the old app's sheet also has an
// "HR ID" column that its own backend silently discards (StaffBatchRegistrationRequest has
// no matching field) — the server always auto-generates the id. Not carried forward as a
// field here since it would be discarded either way; documented in MIGRATION.md.
const COLUMN_MAP = {
  "hr name": "staffName",
  gender: "gender",
  religion: "religion",
  "father's name": "fatherName",
  "mother's name": "motherName",
  "mobile no.": "mobileNumber",
};

/**
 * Direct port of digicampusbd's `staff-multiple-registration` ("Excel
 * Entry"). Confirmed via `StaffRegistrationService.staffBatchRegistration`
 * that the old app parses the uploaded .xlsx entirely client-side and posts
 * plain per-row JSON — the exact same shape the already-existing single-
 * staff `POST /api/staff` (createStaff) already accepts, which already
 * auto-generates `customStaffId` via a correctly-unsigned-cast native query
 * (this app's own `findMaxCustomStaffId`, unlike the old app's own
 * lexicographic-max bug on this same path — see MIGRATION.md). **Zero new
 * backend** — client-side-parse-then-call-the-existing-endpoint once per row.
 * Each parsed row keeps the old app's own per-row "Check" checkbox (checked
 * by default) so a bad row from the sheet can be excluded before saving,
 * rather than committing every parsed row unconditionally.
 */
export default function StaffExcelRegistrationPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError("");
    setResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = raw.map((r) => {
          const row = { checked: true };
          Object.keys(r).forEach((header) => {
            const key = COLUMN_MAP[header.trim().toLowerCase()];
            if (key) row[key] = String(r[header]).trim();
          });
          return row;
        });
        setRows(mapped);
      } catch (err) {
        setError("Failed to parse the Excel file. Please use the provided template.");
      }
    };
    reader.readAsBinaryString(file);
  };

  const updateCell = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async () => {
    setError("");
    const selectedRows = rows.filter((r) => r.checked);
    if (selectedRows.length === 0) {
      setError("Please select at least one row to save.");
      return;
    }
    setSubmitting(true);
    let success = 0;
    const failures = [];
    for (const row of selectedRows) {
      try {
        await createStaff({
          staffName: row.staffName,
          gender: row.gender,
          religion: row.religion,
          fatherName: row.fatherName,
          motherName: row.motherName,
          mobileNumber: row.mobileNumber,
        });
        success++;
      } catch (err) {
        failures.push(`${row.staffName}: ${(err.response && err.response.data && err.response.data.message) || "failed"}`);
      }
    }
    setResult({ success, total: selectedRows.length, failures });
    setSubmitting(false);
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Staff Excel Entry</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {result && (
        <div>
          <p>
            Registered {result.success} of {result.total} selected staff.
          </p>
          {result.failures.length > 0 && (
            <ul>
              {result.failures.map((f, i) => (
                <li key={i} style={{ color: "var(--color-danger)" }}>
                  {f}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="form-row">
        <label>
          Upload Excel (.xlsx) — columns: HR Name, Gender, Religion, Father's Name, Mother's Name, Mobile No.
          <input type="file" accept=".xlsx,.xls" onChange={handleFile} />
        </label>
      </div>
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={rows.every((r) => r.checked)}
                    onChange={(e) => setRows((prev) => prev.map((r) => ({ ...r, checked: e.target.checked })))}
                  />
                </th>
                <th>Name</th>
                <th>Gender</th>
                <th>Religion</th>
                <th>Father's Name</th>
                <th>Mother's Name</th>
                <th>Mobile</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input type="checkbox" checked={!!r.checked} onChange={(e) => updateCell(i, "checked", e.target.checked)} />
                  </td>
                  <td>
                    <input value={r.staffName || ""} onChange={(e) => updateCell(i, "staffName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.gender || ""} onChange={(e) => updateCell(i, "gender", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.religion || ""} onChange={(e) => updateCell(i, "religion", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.fatherName || ""} onChange={(e) => updateCell(i, "fatherName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.motherName || ""} onChange={(e) => updateCell(i, "motherName", e.target.value)} />
                  </td>
                  <td>
                    <input value={r.mobileNumber || ""} onChange={(e) => updateCell(i, "mobileNumber", e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && (
        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting..." : `Register ${rows.filter((r) => r.checked).length} Staff`}
        </button>
      )}
    </MainLayout>
  );
}
